import type { CompetitorOutcome, Report } from "@crux/contracts";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * Reports, and the competitors and questions on them.
 */

export async function fetchReports(workspaceId: string): Promise<readonly Report[]> {
  const response = await api.GET("/api/workspaces/{workspaceId}/reports", {
    params: { path: { workspaceId } },
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "load your reports"));
  }
  return response.data.reports;
}

export async function createReport(
  workspaceId: string,
  body: { name: string; brandId: string },
): Promise<Report> {
  const response = await api.POST("/api/workspaces/{workspaceId}/reports", {
    params: { path: { workspaceId } },
    body,
  });
  if (response.data === undefined) {
    // The server names the brand that is already tracked; its sentence is the
    // one to show.
    if (response.response.status === 409) {
      throw new Error(response.error.message);
    }
    throw new Error(problem(response.response.status, "create that report"));
  }
  return response.data;
}

export async function addCompetitors(
  workspaceId: string,
  reportId: string,
  competitors: readonly { name: string; website: string }[],
): Promise<readonly CompetitorOutcome[]> {
  const response = await api.POST(
    "/api/workspaces/{workspaceId}/reports/{reportId}/competitors",
    {
      params: { path: { workspaceId, reportId } },
      body: { competitors: [...competitors] },
    },
  );
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "add those competitors"));
  }
  return response.data.results;
}

export async function subscribePrompts(
  workspaceId: string,
  reportId: string,
  promptIds: readonly string[],
): Promise<{ promptCount: number; added: number; alreadySubscribed: number }> {
  const response = await api.POST(
    "/api/workspaces/{workspaceId}/reports/{reportId}/prompts",
    {
      params: { path: { workspaceId, reportId } },
      body: { promptIds: [...promptIds] },
    },
  );
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "attach those questions"));
  }
  return response.data;
}

export async function renameReport(
  workspaceId: string,
  reportId: string,
  name: string,
): Promise<Report> {
  const response = await api.PATCH("/api/workspaces/{workspaceId}/reports/{reportId}", {
    params: { path: { workspaceId, reportId } },
    body: { name },
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "rename this report"));
  }
  return response.data;
}

export async function deleteReport(
  workspaceId: string,
  reportId: string,
): Promise<{ brandDeleted: boolean; capturesCancelled: number }> {
  const response = await api.DELETE("/api/workspaces/{workspaceId}/reports/{reportId}", {
    params: { path: { workspaceId, reportId } },
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "delete this report"));
  }
  return response.data;
}

/**
 * Pauses questions on brand reports. The reports keep their answers; adding a
 * question to a report again resumes it.
 */
export async function removePromptsFromReports(
  workspaceId: string,
  body: { reportIds: string[]; promptIds: string[] },
): Promise<{ removed: number; capturesCancelled: number }> {
  const response = await api.POST("/api/workspaces/{workspaceId}/report-prompts/remove", {
    params: { path: { workspaceId } },
    body,
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "pause those questions"));
  }
  return response.data;
}

export async function removeCompetitor(
  workspaceId: string,
  reportId: string,
  brandId: string,
): Promise<Report> {
  const response = await api.DELETE(
    "/api/workspaces/{workspaceId}/reports/{reportId}/competitors/{brandId}",
    { params: { path: { workspaceId, reportId, brandId } } },
  );
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "remove that competitor"));
  }
  return response.data;
}

/** Pauses every question the report tracks. Its answers so far stay. */
export async function pauseReport(
  workspaceId: string,
  reportId: string,
): Promise<{ removed: number; capturesCancelled: number }> {
  const response = await api.POST(
    "/api/workspaces/{workspaceId}/reports/{reportId}/pause",
    {
      params: { path: { workspaceId, reportId } },
    },
  );
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "pause this report"));
  }
  return response.data;
}

/** Tracks every question the report paused again, and asks for them now. */
export async function resumeReport(
  workspaceId: string,
  reportId: string,
): Promise<{ promptCount: number; added: number; alreadySubscribed: number }> {
  const response = await api.POST(
    "/api/workspaces/{workspaceId}/reports/{reportId}/resume",
    {
      params: { path: { workspaceId, reportId } },
    },
  );
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "resume this report"));
  }
  return response.data;
}
